/**
 * GovernancePackPanel.jsx — RC GOVERNANCE.PACKS.G1.
 *
 * Operational closure of the post-evaluation workflow. Replaces the
 * `GovernancePackPlaceholder` shipped in RC EMPLOYER.WORKFLOW.P0.
 *
 * Surfaces inline below the dossier list on `/employer/roles/{id}`
 * (Uploaded Dossiers tab). Walks the operator through:
 *
 *   draft selection  → CREATE draft
 *                    → FINALISE  (allowance consumed atomically)
 *                    → EXPORT PDF
 *
 * Strict scope:
 *   • No fake actions. CTAs only render once the operator can
 *     truthfully act on them.
 *   • Allowance consumed ONLY at finalise (server-enforced).
 *   • Finalised packs are immutable — UI shows them read-only.
 *   • PDF export streams a real reportlab-generated artefact.
 *   • Failure surfaces honestly:
 *       MIGRATION_PENDING → calm hint to ops
 *       PACK_BUDGET_EXHAUSTED → directs to billing
 *
 * Out of scope (deferred):
 *   • DOCX export, email send, signatures, cross-role packs.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FileCheck2, Loader2, FileDown, Plus, Trash2, ShieldCheck, AlertCircle,
} from 'lucide-react';

import { apiFetch } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { playCompletionChime } from '@/lib/completionChime';
import Disclaimer from '@/components/Disclaimer';


const STATUS_LABEL = {
  draft:     { label: 'Draft',     tone: 'amber' },
  finalised: { label: 'Finalised', tone: 'emerald' },
  exported:  { label: 'Exported',  tone: 'emerald' },
  revoked:   { label: 'Revoked',   tone: 'rose' },
};


function ShortId({ value }) {
  if (!value) return <span>—</span>;
  return (
    <code className="font-mono text-[10px] text-ndor-ink-muted">
      {String(value).slice(0, 8)}…
    </code>
  );
}


function StatusBadge({ status }) {
  const meta = STATUS_LABEL[status] || { label: status, tone: 'slate' };
  const cls = {
    emerald: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300',
    amber:   'border-amber-500/40 bg-amber-500/10 text-amber-300',
    rose:    'border-rose-500/40 bg-rose-500/10 text-rose-300',
    slate:   'border-ndor-edge text-ndor-ink-secondary',
  }[meta.tone];
  return (
    <Badge
      variant="outline"
      className={`text-[10px] uppercase tracking-[0.14em] ${cls}`}
      data-testid={`governance-pack-status-${status}`}
    >
      {meta.label}
    </Badge>
  );
}


export default function GovernancePackPanel({ roleId, items }) {
  // Eligible candidates: shortlisted OR review with completed evaluation.
  const eligible = useMemo(() => {
    if (!Array.isArray(items)) return [];
    return items.filter((c) =>
      c.evaluation_status === 'complete'
      && (c.governance_status === 'shortlisted'
          || c.governance_status === 'review')
      && !c.erased_at,
    );
  }, [items]);

  const shortlistedCount = eligible.filter(
    (c) => c.governance_status === 'shortlisted',
  ).length;
  const reviewCount = eligible.filter(
    (c) => c.governance_status === 'review',
  ).length;

  // Pack lifecycle state.
  const [packs, setPacks]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [loadError, setLoadError]       = useState(null);
  const [migrationPending, setMP]       = useState(false);

  // Draft-builder state.
  const [selected, setSelected]         = useState({}); // { [cid]: true }
  const [drafting, setDrafting]         = useState(false);

  // Per-pack action state.
  const [actionBusy, setActionBusy]     = useState({}); // { [pack_id]: 'finalise'|'export'|'delete' }

  const refreshPacks = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const j = await apiFetch(`/api/employer/v1/roles/${roleId}/governance-packs`);
      setPacks(Array.isArray(j?.items) ? j.items : []);
      setMP(Boolean(j?.migration_pending));
    } catch (err) {
      setLoadError(err?.message || 'Failed to load governance packs.');
    } finally {
      setLoading(false);
    }
  }, [roleId]);

  useEffect(() => { refreshPacks(); }, [refreshPacks]);

  const toggle = (cid) => {
    setSelected((p) => ({ ...p, [cid]: !p[cid] }));
  };
  const selectAll = () => {
    const next = {};
    for (const c of eligible) next[c.id] = true;
    setSelected(next);
  };
  const clearAll = () => setSelected({});
  const selectedCount = Object.values(selected).filter(Boolean).length;

  const onCreateDraft = useCallback(async () => {
    const ids = Object.entries(selected)
      .filter(([, v]) => v)
      .map(([k]) => k);
    if (ids.length === 0) {
      toast.error('Select at least one candidate.');
      return;
    }
    setDrafting(true);
    try {
      await apiFetch(`/api/employer/v1/roles/${roleId}/governance-packs`, {
        method: 'POST',
        body:   JSON.stringify({ candidate_ids: ids, kind: 'role_governance' }),
      });
      toast.success(`Draft created with ${ids.length} candidate${ids.length === 1 ? '' : 's'}.`);
      setSelected({});
      await refreshPacks();
    } catch (err) {
      const d = err?.data?.detail || {};
      const code = d.code || 'DRAFT_FAILED';
      if (code === 'GOVERNANCE_PACK_MIGRATION_PENDING') {
        setMP(true);
        toast.error('Governance pack storage migration pending — see admin hint below.');
      } else {
        toast.error(`${code}: ${d.message || err?.message || 'Draft failed.'}`);
      }
    } finally {
      setDrafting(false);
    }
  }, [selected, roleId, refreshPacks]);

  const onFinalise = useCallback(async (packId) => {
    setActionBusy((p) => ({ ...p, [packId]: 'finalise' }));
    try {
      const r = await apiFetch(
        `/api/employer/v1/governance-packs/${packId}/finalise`,
        { method: 'POST' },
      );
      if (r?.changed === false) {
        toast.message('Pack was already finalised.');
      } else {
        toast.success('Pack finalised — content is now immutable.');
      }
      await refreshPacks();
    } catch (err) {
      const d = err?.data?.detail || {};
      const code = d.code || 'FINALISE_FAILED';
      if (code === 'PACK_BUDGET_EXHAUSTED') {
        toast.error('Governance pack allowance exhausted for this period. Review your plan in Billing.');
      } else {
        toast.error(`${code}: ${d.message || d.detail || err?.message || 'Finalise failed.'}`);
      }
    } finally {
      setActionBusy((p) => { const n = { ...p }; delete n[packId]; return n; });
    }
  }, [refreshPacks]);

  const onExport = useCallback(async (packId) => {
    setActionBusy((p) => ({ ...p, [packId]: 'export' }));
    try {
      // Stream the PDF — apiFetch returns parsed JSON by default, so
      // we use a raw fetch here to download the bytes.
      //
      // Auth: pull the live access token from supabase-js (the JWT
      // is stored under `sb-<project-ref>-auth-token`, NOT under
      // `localStorage['jwt_token']`). This mirrors what `apiFetch`
      // does internally for every other authenticated call.
      const base = process.env.REACT_APP_BACKEND_URL || '';
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token || '';
      if (!token) {
        toast.error('Your session has expired. Please sign in again.');
        return;
      }
      const res = await fetch(
        `${base}/api/employer/v1/governance-packs/${packId}/export`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `Export failed (${res.status}).`);
      }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href = url;
      a.download = `governance-pack-${packId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      // Long-running completion notification only.
      // Fired here, AFTER the blob has been handed to the browser,
      // not when the export RPC returns — that way we don't chime
      // on a 4xx export-failure that still resolves the promise.
      try { playCompletionChime(); } catch (_) { /* silent */ }
      toast.success('Governance pack downloaded.');
      await refreshPacks();
    } catch (err) {
      toast.error(err?.message || 'Export failed.');
    } finally {
      setActionBusy((p) => { const n = { ...p }; delete n[packId]; return n; });
    }
  }, [refreshPacks]);

  // ── empty-state guard ──────────────────────────────────────
  if (eligible.length === 0 && packs.length === 0 && !loading) {
    return (
      <Card
        className="border-ndor-edge bg-ndor-surface"
        data-testid="governance-pack-empty"
      >
        <CardContent className="px-5 py-4 sm:px-6 sm:py-5">
          <div className="flex items-start gap-3">
            <div
              className="shrink-0 w-9 h-9 rounded-md border border-ndor-edge bg-ndor-surface-alt/50 flex items-center justify-center"
              aria-hidden
            >
              <FileCheck2 className="h-4 w-4 text-ndor-ink-secondary" />
            </div>
            <div className="min-w-0 flex-1 space-y-1.5">
              <h3 className="text-sm font-medium text-ndor-ink">
                Governance pack
              </h3>
              <p className="text-xs text-ndor-ink-secondary leading-relaxed">
                Governance packs become available once candidates are
                shortlisted or flagged for review. Run evaluations on the
                dossiers above to begin.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      className="border-ndor-edge bg-ndor-surface"
      data-testid="governance-pack-panel"
    >
      <CardContent className="px-5 py-4 sm:px-6 sm:py-5 space-y-5">
        <div className="flex items-start gap-3">
          <div
            className="shrink-0 w-9 h-9 rounded-md border border-ndor-edge bg-ndor-surface-alt/50 flex items-center justify-center"
            aria-hidden
          >
            <FileCheck2 className="h-4 w-4 text-ndor-ink-secondary" />
          </div>
          <div className="min-w-0 flex-1 space-y-1">
            <h3 className="text-sm font-medium text-ndor-ink">
              Governance pack
            </h3>
            <p className="text-xs text-ndor-ink-secondary leading-relaxed">
              The formal governance artefact operationalising the
              hiring decision. Finalised packs are immutable and
              count against your plan&apos;s monthly allowance.
            </p>
          </div>
        </div>

        {/* MIGRATION HINT (operator-internal) */}
        {migrationPending ? (
          <div
            className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 text-[11px] text-amber-200 flex items-start gap-2"
            data-testid="governance-pack-migration-hint"
          >
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
            <span>
              Governance pack storage migration pending. Apply{' '}
              <code className="font-mono">
                backend/migrations/130_emp_governance_packs_payload.sql
              </code>{' '}
              via Supabase Studio to enable this surface.
            </span>
          </div>
        ) : null}

        {/* DRAFT BUILDER */}
        {eligible.length > 0 && (
          <div
            className="rounded-md border border-ndor-edge bg-ndor-bg/30 p-3 space-y-3"
            data-testid="governance-pack-draft-builder"
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[11px] uppercase tracking-[0.16em] text-ndor-ink-secondary font-medium">
                Build a new pack
              </p>
              <div className="flex items-center gap-2 text-[10px] text-ndor-ink-secondary">
                <span data-testid="governance-pack-eligible-strip">
                  Eligible · {shortlistedCount} shortlisted · {reviewCount} review
                </span>
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-ndor-ink-secondary hover:text-ndor-ink underline-offset-2 hover:underline"
                  data-testid="governance-pack-select-all"
                >
                  Select all
                </button>
                <span aria-hidden>·</span>
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-ndor-ink-secondary hover:text-ndor-ink underline-offset-2 hover:underline"
                  data-testid="governance-pack-clear-all"
                >
                  Clear
                </button>
              </div>
            </div>

            <ul
              className="space-y-1.5 max-h-[200px] overflow-y-auto pr-1"
              data-testid="governance-pack-eligible-list"
            >
              {eligible.map((c) => {
                const isOn = Boolean(selected[c.id]);
                const verdictPill =
                  c.governance_status === 'shortlisted' ? 'emerald'
                  : c.governance_status === 'review'    ? 'amber'
                  : 'slate';
                const pillCls = {
                  emerald: 'bg-emerald-500/10 text-emerald-300 border-emerald-500/40',
                  amber:   'bg-amber-500/10 text-amber-300 border-amber-500/40',
                  slate:   'bg-ndor-bg/40 text-ndor-ink-secondary border-ndor-edge',
                }[verdictPill];
                return (
                  <li
                    key={c.id}
                    className="flex items-center gap-3 px-2 py-1.5 rounded border border-transparent hover:border-ndor-edge"
                    data-testid={`governance-pack-eligible-row-${c.id}`}
                  >
                    <Checkbox
                      checked={isOn}
                      onCheckedChange={() => toggle(c.id)}
                      data-testid={`governance-pack-eligible-checkbox-${c.id}`}
                    />
                    <ShortId value={c.id} />
                    <span className="flex-1 text-xs text-ndor-ink-secondary truncate">
                      {c.original_filename || '—'}
                    </span>
                    <span
                      className={`text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border ${pillCls}`}
                    >
                      {c.governance_status}
                    </span>
                  </li>
                );
              })}
            </ul>

            <div className="flex items-center justify-between gap-2 pt-1">
              <span
                className="text-xs text-ndor-ink-secondary"
                data-testid="governance-pack-selected-count"
              >
                {selectedCount} selected
              </span>
              <Button
                size="sm"
                disabled={selectedCount === 0 || drafting}
                onClick={onCreateDraft}
                className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted"
                data-testid="governance-pack-create-draft-btn"
              >
                {drafting ? (
                  <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Creating…</>
                ) : (
                  <><Plus className="mr-1.5 h-3.5 w-3.5" />Create governance pack draft</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* EXISTING PACKS LIST */}
        <div className="space-y-2" data-testid="governance-pack-list">
          <p className="text-[11px] uppercase tracking-[0.16em] text-ndor-ink-secondary font-medium">
            Packs for this role
          </p>
          {loading ? (
            <p className="text-xs text-ndor-ink-muted" data-testid="governance-pack-loading">
              Loading…
            </p>
          ) : loadError ? (
            <p className="text-xs text-rose-300" data-testid="governance-pack-load-error">
              {loadError}
            </p>
          ) : packs.length === 0 ? (
            <p className="text-xs text-ndor-ink-muted" data-testid="governance-pack-list-empty">
              No packs yet — assemble your first one above.
            </p>
          ) : (
            <ul className="space-y-2">
              {packs.map((p) => {
                const busy   = actionBusy[p.id];
                const isDraft     = p.pack_status === 'draft';
                const isFinalised = p.pack_status === 'finalised' || p.pack_status === 'exported';
                return (
                  <li
                    key={p.id}
                    className="rounded-md border border-ndor-edge bg-ndor-bg/30 px-3 py-2.5 flex items-center gap-3 flex-wrap"
                    data-testid={`governance-pack-row-${p.id}`}
                  >
                    <ShortId value={p.id} />
                    <StatusBadge status={p.pack_status} />
                    <span className="text-xs text-ndor-ink-secondary">
                      {p.included_count} candidate{p.included_count === 1 ? '' : 's'}
                    </span>
                    <span className="text-[10px] text-ndor-ink-muted">
                      created {p.created_at ? new Date(p.created_at).toLocaleString() : '—'}
                    </span>
                    {p.finalised_at ? (
                      <span className="text-[10px] text-emerald-300/80 flex items-center gap-1">
                        <ShieldCheck className="h-3 w-3" />
                        finalised {new Date(p.finalised_at).toLocaleString()}
                      </span>
                    ) : null}
                    <span className="flex-1" />
                    {isDraft ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={Boolean(busy)}
                        onClick={() => onFinalise(p.id)}
                        className="border-ndor-edge text-ndor-ink hover:bg-ndor-surface-alt"
                        data-testid={`governance-pack-finalise-${p.id}`}
                      >
                        {busy === 'finalise' ? (
                          <><Loader2 className="mr-1.5 h-3 w-3 animate-spin" />Finalising…</>
                        ) : (
                          <><ShieldCheck className="mr-1.5 h-3 w-3" />Finalise pack</>
                        )}
                      </Button>
                    ) : null}
                    {isFinalised ? (
                      <Button
                        size="sm"
                        disabled={Boolean(busy)}
                        onClick={() => onExport(p.id)}
                        className="bg-ndor-ink text-ndor-surface hover:bg-ndor-ink-muted disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid={`governance-pack-export-${p.id}`}
                      >
                        {busy === 'export' ? (
                          <><Loader2 className="mr-1.5 h-3 w-3 animate-spin" />Exporting…</>
                        ) : (
                          <><FileDown className="mr-1.5 h-3 w-3" />Export PDF</>
                        )}
                      </Button>
                    ) : null}
                    {isDraft ? (
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled
                        className="text-ndor-ink-muted/60 hover:text-ndor-ink-muted/60 cursor-not-allowed"
                        title="Draft delete ships in the next slice."
                        data-testid={`governance-pack-delete-disabled-${p.id}`}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p
          className="text-[10px] text-ndor-ink-muted leading-relaxed pt-1 border-t border-ndor-edge"
          data-testid="governance-pack-footer"
        >
          Allowance is consumed only when a pack is finalised.
          Re-exporting a finalised pack does not consume additional
          allowance. Pack content is frozen at finalisation.
        </p>

        {/* RC 1.61 P1.1 (operator-approved 2026-05-29) — institutional
            disclaimer footer. Same component + same `DOCTRINE_DISCLAIMER`
            string surfaced on Account Plan and Employer Billing, so the
            doctrine line stays identical across every in-app surface.
            Mirrored into the exported PDF by `_build_governance_pack_markdown`
            in backend/routes/employer_governance_packs.py. */}
        <Disclaimer
          variant="block"
          testId="governance-pack-disclaimer"
          className="mt-4"
        />
      </CardContent>
    </Card>
  );
}
